"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn, arrayUtils } from "@/lib/utils.js";
import { Button } from "@/components/ui/button.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover.jsx";
import { useDrawerPortalContainer } from "@/components/ui/drawer";
import { countBubble } from "@/lib/status-styles.js";

/**
 * Combobox component for searchable dropdowns with multi-select support
 * @param {Object} props - Component props
 * @param {string|string[]} [props.value] - Current selected value(s)
 * @param {function} [props.onValueChange] - Callback when value changes
 * @param {Array} [props.options] - Array of options to display
 * @param {number|string} [props.options[].count] - Optional count badge shown beside option label
 * @param {string} [props.options[].type] - Optional option type (e.g. 'divider')
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.searchPlaceholder] - Search input placeholder
 * @param {string} [props.emptyText] - Text to show when no options match
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.disabled] - Whether the combobox is disabled
 * @param {boolean} [props.multiple] - Whether to allow multiple selections
 * @param {boolean} [props.closeOnSelect] - Whether to close popover after single selection
 * @param {React.ReactNode} [props.footerContent] - Optional footer content inside dropdown
 * @returns {JSX.Element} Combobox component
 */
export function Combobox({
  value,
  onValueChange,
  options = [],
  placeholder = "Select option...",
  searchPlaceholder = "Search...",
  emptyText = "No option found.",
  className,
  disabled = false,
  multiple = false,
  closeOnSelect = true,
  footerContent,
  ...props
}) {
  const drawerPortalContainer = useDrawerPortalContainer();
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");

  const handleSelect = (currentValue) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(currentValue)
        ? currentValues.filter((v) => v !== currentValue)
        : [...currentValues, currentValue];
      onValueChange?.(newValues);
    } else {
      onValueChange?.(currentValue === value ? "" : currentValue);
      if (closeOnSelect) {
        setOpen(false);
      }
    }
  };

  const getDisplayValue = () => {
    if (multiple) {
      const selectedValues = Array.isArray(value) ? value : [];
      if (arrayUtils.isEmpty(selectedValues)) return placeholder;
      if (!arrayUtils.isEmpty(selectedValues) && selectedValues.length === 1) {
        return (
          options.find((option) => option.value === selectedValues[0])?.label
          || selectedValues[0]
        );
      }
      return `${selectedValues.length} selected`;
    }
    const isEmpty = value === undefined || value === null || value === "";
    return isEmpty
      ? placeholder
      : (options.find((option) => option.value === value)?.label
          ?? placeholder);
  };

  const hasValue = multiple
    ? Array.isArray(value) && value.length > 0
    : value !== undefined && value !== null && value !== "";

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    const baseOptions = !searchTerm
      ? options
      : options.filter(
          (option) =>
            option.type === "divider"
            || option.label.toLowerCase().includes(searchTerm.toLowerCase())
        );

    // Clean up dividers so we do not render consecutive or edge dividers.
    return baseOptions.filter((option, index, array) => {
      if (option.type !== "divider") {
        return true;
      }

      const previousOption = array[index - 1];
      const nextOption = array[index + 1];
      const isLeadingDivider = index === 0;

      if (isLeadingDivider) {
        return !!nextOption && nextOption.type !== "divider";
      }

      return (
        previousOption.type !== "divider"
        && nextOption
        && nextOption.type !== "divider"
      );
    });
  }, [options, searchTerm]);

  return (
    <Popover
      open={open}
      onOpenChange={(newOpen) => {
        setOpen(newOpen);
        if (!newOpen) {
          setSearchTerm("");
        }
      }}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className={cn(
            "w-full justify-between bg-muted border border-border rounded-md px-3 py-2",
            "text-sm font-normal leading-6 text-foreground",
            "h-[calc(1.5em_+_0.75rem_+_2px)]",
            "transition-all duration-150 ease-in-out",
            "focus:outline-none focus:border-border focus:ring-1 focus:ring-border",
            "focus-visible:ring-border focus-visible:ring-offset-0",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "hover:bg-muted/80",
            "min-w-0",
            className
          )}
          disabled={disabled}
          {...props}>
          <span
            className={cn(
              "truncate flex-1 text-left",
              hasValue ? "text-foreground" : "text-muted-foreground"
            )}>
            {getDisplayValue()}
          </span>
          <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 text-muted-foreground' />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        {...(drawerPortalContainer
          ? { container: drawerPortalContainer }
          : {})}
        className={cn(
          "min-w-[var(--radix-popover-trigger-width)] max-w-[min(20rem,calc(100vw-1rem))] w-max p-0",
          drawerPortalContainer ? "z-panel-popover" : "z-popover",
          "max-h-[min(24rem,var(--radix-popover-content-available-height))] overflow-hidden"
        )}
        align='start'>
        <div className='bg-popover border border-border rounded-md shadow-lg h-full max-h-[min(24rem,var(--radix-popover-content-available-height))] overflow-hidden flex flex-col'>
          <div className='p-2 border-b border-border'>
            <input
              type='text'
              placeholder={searchPlaceholder}
              className='w-full px-2 py-1 text-sm border border-input rounded bg-card text-foreground focus:outline-none focus:border-border focus:ring-1 focus:ring-border'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className='flex-1 min-h-0 overflow-y-auto'>
            {arrayUtils.isEmpty(filteredOptions) ? (
              <div className='p-4 text-sm text-muted-foreground text-center'>
                {emptyText}
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                if (option.type === "divider") {
                  return (
                    <div
                      key={option.value || option.label}
                      className={cn(
                        "px-3 py-2 bg-muted",
                        index > 0 && "border-t border-border"
                      )}>
                      <span className='text-[11px] font-medium uppercase tracking-wide text-muted-foreground'>
                        {option.label}
                      </span>
                    </div>
                  );
                }

                const isSelected = multiple
                  ? Array.isArray(value) && value.includes(option.value)
                  : value === option.value;

                return (
                  <div
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      "flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted transition-colors",
                      isSelected && "bg-muted/80"
                    )}>
                    <div className='flex min-w-0 items-center'>
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 text-primary",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className='truncate'>{option.label}</span>
                    </div>
                    {(typeof option.count === "number"
                      || typeof option.count === "string") && (
                      <Badge
                        variant='secondary'
                        className={cn("ml-2", countBubble)}>
                        {option.count}
                      </Badge>
                    )}
                  </div>
                );
              })
            )}
          </div>
          {footerContent && (
            <div className='min-w-0 border-t border-border p-2'>
              {footerContent}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
